/* eslint-disable arrow-body-style */
// Once you know how one prop or component arrow function works
// you know how they all work (not really unique)
/* eslint-disable arrow-parens */
/* eslint-disable max-len */
// ^ This prevents errors in the `component` property of each route config
import Router from 'vue-router'
// Helpers
import {
  Debtor,
} from './utils/api'
import {
  isMobile,
} from './utils/helpers'
import { authGuard } from './utils/auth/authGuard'

// If getInstance() is called during a redirect or "fresh" navigation, it will return undefined beacuse
// auth0Plugin hasn't been initiated yet (the route is resolved first, then the route component)
// i.e. use beforeEnter instead of redirect if you need to read a value from getInstance()
import { getInstance } from './utils/auth/auth0Plugin'

const userType = {
  isClient: () => {
    if (getInstance()?.user.client) {
      return true
    }
    return false
  },
  isEmployee: () => {
    if (getInstance()?.user.employee) {
      return true
    }
    return false
  },
  isMaster: () => {
    if (getInstance()?.user.employee && getInstance()?.user.employee.is_master) {
      return true
    }
    return false
  },
}

/*
 * Meta fields:
 * (ex. adding `requiresClient` means the user must have a client's or higher level of permission)
 * A. User must be a client: `requiresClient: true`
 * B. User must be an employee: `requiresEmployee: true`
 * C. User must be a master employee: `requiresMaster: true`
 */
const router = new Router({
  mode: 'history',
  routes: [
    {
      meta: {
        maintenanceFriendly: true,
        mobileFriendly: false,
        requiresAuth: true,
        requiresMaster: true,
        title: '',
      },
      name: 'admin',
      path: '/admin',
      props: (route) => ({
        ...route.params,
      }),
      redirect: { name: 'admin-debtor-upload' },
      component: () => import(/* webpackChunkName: "admin" */ './views/admin/admin.vue'),
      children: [
        {
          meta: {
            title: 'Admin Reserves',
          },
          name: 'admin-reserves',
          path: 'admin-reserves',
          props: true,
          component: () => import(/* webpackChunkName: "admin-reserves" */'./views/admin/admin-reserves.vue'),
        },
        {
          meta: {
            title: 'Debtor Upload',
          },
          name: 'admin-debtor-upload',
          path: 'admin-debtor-upload',
          props: true,
          component: () => import(/* webpackChunkName: "debtor-upload" */'./views/admin/admin-debtor-upload.vue'),
        },
        {
          meta: {
            title: 'Maintenance Mode',
          },
          name: 'admin-maintenance',
          path: 'admin-maintenance',
          props: true,
          component: () => import(/* webpackChunkName: "admin-maintenance" */'./views/admin/admin-maintenance.vue'),
        },
        {
          meta: {
            title: 'Announcements',
          },
          name: 'admin-announcements',
          path: 'admin-announcements',
          props: true,
          component: () => import(/* webpackChunkName: "admin-announcements" */'./views/admin/admin-announcements.vue'),
        },
      ],
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: false,
        requiresAuth: true,
        requiresMaster: true,
        title: 'Buyouts',
      },
      name: 'buyouts',
      path: '/buyouts',
      props: true,
      component: () => import(/* webpackChunkName: "buyouts" */ './views/buyouts.vue'),
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: true,
        guest: true,
        title: 'Auth0 Redirect',
      },
      name: 'auth0-callback',
      path: '/auth-0-callback*',
      props: true,
      component: () => import(/* webpackChunkName: "auth0-redirect" */ './views/auth/redirect.vue'),
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: false,
        requiresAuth: true,
        requiresClient: true,
        title: '',
      },
      name: 'client',
      path: '/client/:id',
      props: (route) => ({
        ...route.params,
        userIsClient: userType.isClient(),
      }),
      redirect: { name: 'client-activity' },
      component: () => import(/* webpackChunkName: "client" */ './views/client/profile/client.vue'),
      children: [
        {
          meta: {
            requiresEmployee: true,
            title: 'Client Profile',
          },
          name: 'client-profile',
          path: 'profile',
          component: () => import(/* webpackChunkName: "client-profile" */ './views/client/profile/client-profile.vue'),
        },
        {
          meta: {
            requiresEmployee: true,
            title: 'Client Contacts',
          },
          name: 'client-contacts',
          path: 'contacts',
          props: () => ({
            pagePOV: 'client',
          }),
          component: () => import(/* webpackChunkName: "user-profile-contacts" */ './components/user-profile-contacts.vue'),
        },
        {
          meta: {
            title: 'Client Activity',
          },
          name: 'client-activity',
          path: 'activity',
          props: () => ({
            pagePOV: 'client',
            userIsClient: userType.isClient(),
            userIsEmployee: userType.isEmployee(),
          }),
          component: () => import(/* webpackChunkName: "client-activity" */ './components/user-activity.vue'),
        },
        {
          meta: {
            requiresEmployee: true,
            title: 'Processing Notes',
          },
          name: 'client-processing-notes',
          path: 'notes',
          component: () => import(/* webpackChunkName: "client-processing-notes" */ './views/client/profile/client-processing-notes.vue'),
        },
        {
          meta: {
            requiresEmployee: true,
            title: 'Client Docs',
          },
          name: 'client-docs',
          path: 'docs',
          component: () => import(/* webpackChunkName: "user-docs" */ './views/user-docs.vue'),
        },
        {
          meta: {
            requiresEmployee: true,
            title: 'Client Assignments',
          },
          name: 'client-assignments',
          path: 'assignments',
          props: () => ({
            pagePOV: 'client',
            userIsClient: userType.isClient(),
            userIsEmployee: userType.isEmployee(),
          }),
          component: () => import(/* webpackChunkName: "user-profile-assignments" */ './components/user-profile-assignments.vue'),
        },
        {
          meta: {
            requiresEmployee: true,
            title: 'Client Status',
          },
          name: 'client-status',
          path: 'status',
          props: (route) => ({
            ...route.params,
            userIsClient: userType.isClient(),
            userIsEmployee: userType.isEmployee(),
          }),
          component: () => import(/* webpackChunkName: "client-status" */ './views/client/profile/client-status.vue'),
        },
      ],
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: false,
        requiresAuth: true,
        requiresEmployee: true,
        title: 'Create Client',
      },
      name: 'create-client',
      path: '/create-client',
      component: () => import(/* webpackChunkName: "create-client" */ './views/client/create/create-client.vue'),
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: false,
        requiresAuth: true,
        requiresEmployee: true,
        title: 'Create Debtor',
      },
      name: 'create-debtor',
      path: '/create-debtor',
      component: () => import(/* webpackChunkName: "create-debtor" */ './views/debtor/create/create-debtor.vue'),
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: false,
        requiresAuth: true,
        requiresMaster: true,
        title: 'Create Employee',
      },
      name: 'create-employee',
      path: '/create-employee',
      component: () => import(/* webpackChunkName: "create-employee" */ './views/employee/create-edit-employee.vue'),
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: true,
        requiresAuth: true,
        requiresClient: true,
        title: 'Credit Check',
      },
      name: 'credit-check',
      path: '/credit-check',
      props: (route) => ({
        ...route.params,
        userIsClient: userType.isClient(),
      }),
      component: () => import(/* webpackChunkName: "credit-check" */ './views/credit-check.vue'),
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: true,
        requiresAuth: true,
        requiresClient: true,
        title: '',
      },
      name: 'dashboard',
      path: '/dashboard',
      component: () => import(/* webpackChunkName: "dashboard" */ './views/dashboard/dashboard.vue'),
      props: true,
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: true,
        requiresAuth: true,
        requiresClient: true,
        title: '',
      },
      name: 'dashboard-client',
      path: '/dashboard-client/:id',
      props: (route) => ({
        ...route.params,
        userIsClient: userType.isClient(),
      }),
      redirect: { name: 'dashboard-client-notifications' },
      component: () => import(/* webpackChunkName: "dashboard" */ './views/dashboard/dashboard.vue'),
      children: [
        // TODO: https://app.zenhub.com/workspaces/bobtail-5c64d9b0b66dba3bb32bda6e/issues/fs-bobtail/bobtail/2103
        // {
        //   meta: {
        //     mobileFriendly: false,
        //     title: 'Client Drivers',
        //   },
        //   name: 'dashboard-client-drivers',
        //   path: 'client-drivers',
        //   props: (route) => ({
        //     ...route.params,
        //     userIsClient: userType.isClient(),
        //   }),
        //   component: () => import(/* webpackChunkName: "drivers" */ './views/dashboard/dashboard-client-drivers.vue'),
        // },
        {
          meta: {
            mobileFriendly: false,
            title: 'Client Notifications',
          },
          name: 'dashboard-client-notifications',
          path: 'client-notifications',
          props: (route) => ({
            ...route.params,
            userIsClient: userType.isClient(),
          }),
          component: () => import(/* webpackChunkName: "notifications" */ './views/dashboard/dashboard-client-notifications.vue'),
        },
        {
          meta: {
            title: 'Client Transfers',
          },
          name: 'dashboard-client-transfers',
          path: 'client-transfers',
          props: true,
          component: () => import(/* webpackChunkName: "transfers" */ './views/dashboard/dashboard-client-transfers.vue'),
        },
        {
          meta: {
            title: 'Client Reserve History',
          },
          name: 'dashboard-client-reserve-history',
          path: 'client-reserve-history',
          props: (route) => ({
            ...route.params,
            userIsEmployee: userType.isEmployee(),
          }),
          component: () => import(/* webpackChunkName: "reserve-history" */ './views/dashboard/dashboard-client-reserve-history.vue'),
        },
        {
          meta: {
            title: 'Client Banking',
          },
          name: 'dashboard-client-banking',
          path: 'client-banking',
          props: (route) => ({
            ...route.params,
            userIsClient: userType.isClient(),
            userIsEmployee: userType.isEmployee(),
          }),
          component: () => import(/* webpackChunkName: "banking" */ './views/dashboard/dashboard-client-banking.vue'),
        },
      ],
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: false,
        requiresAuth: true,
        requiresEmployee: true,
        title: '',
      },
      name: 'dashboard-bobtail',
      path: '/dashboard-bobtail',
      props: (route) => ({
        ...route.params,
      }),
      redirect: { name: 'dashboard-employee-reserves' },
      component: () => import(/* webpackChunkName: "employee-dashboard" */ './views/dashboard/dashboard.vue'),
      children: [
        {
          meta: {
            title: 'Bobtail Reserves',
          },
          name: 'dashboard-employee-reserves',
          path: 'reserves',
          props: true,
          redirect: { name: 'dashboard-employee-reserves-data' },
          component: () => import(/* webpackChunkName: "employee-reserves" */ './views/dashboard/reserves/reserves.vue'),
          children: [
            {
              meta: {
                title: 'Reserves Data',
              },
              name: 'dashboard-employee-reserves-data',
              path: 'data',
              props: (route) => ({
                ...route.params,
              }),
              component: () => import(/* webpackChunkName: "employee-reserves" */ './views/dashboard/reserves/data.vue'),
            },
            {
              meta: {
                title: 'Reserves Trends',
              },
              name: 'dashboard-employee-reserves-trends',
              path: 'trends',
              props: (route) => ({
                ...route.params,
              }),
              component: () => import(/* webpackChunkName: "employee-reserves" */ './views/dashboard/reserves/trends.vue'),
            },
          ],
        },
        {
          meta: {
            title: 'Bobtail Reports',
          },
          name: 'dashboard-employee-reports',
          path: 'employee-reports',
          props: true,
          component: () => import(/* webpackChunkName: "employee-reports" */'./views/dashboard/dashboard-employee-reports.vue'),
        },
        {
          meta: {
            title: 'Bobtail Collections',
          },
          name: 'dashboard-employee-collections',
          path: 'employee-collections',
          props: true,
          component: () => import(/* webpackChunkName: "employee-collections" */'./views/dashboard/dashboard-employee-collections.vue'),
        },
        {
          meta: {
            title: 'Bobtail Transfers',
          },
          name: 'dashboard-employee-transfers',
          path: 'employee-transfers',
          props: true,
          component: () => import(/* webpackChunkName: "employee-transfers" */'./views/dashboard/dashboard-employee-transfers.vue'),
        },
        {
          meta: {
            title: 'Bobtail Audit Log',
          },
          name: 'dashboard-employee-audit-log',
          path: 'employee-audit-log',
          props: true,
          component: () => import(/* webpackChunkName: "employee-audit-log" */'./views/dashboard/dashboard-employee-audit-log.vue'),
        },
        {
          meta: {
            title: 'Bobtail Quickbooks',
          },
          name: 'dashboard-employee-quickbooks',
          path: 'employee-quickbooks',
          props: true,
          component: () => import(/* webpackChunkName: "employee-quickbooks" */'./views/dashboard/dashboard-employee-quickbooks.vue'),
        },
        {
          meta: {
            title: 'Bobtail Volume',
          },
          name: 'dashboard-employee-volume',
          path: 'employee-volume',
          props: true,
          component: () => import(/* webpackChunkName: "employee-volume" */'./views/dashboard/dashboard-employee-volume.vue'),
        },
      ],
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: false,
        requiresAuth: true,
        requiresEmployee: true,
        title: '',
      },
      name: 'debtor',
      path: '/debtor/:id',
      props: (route) => ({
        ...route.params,
        userIsMaster: userType.isMaster(),
      }),
      redirect: { name: 'debtor-profile' },
      component: () => import(/* webpackChunkName: "debtor" */ './views/debtor/profile/debtor.vue'),
      beforeEnter: (to, from, next) => {
        // Do not allow visiting "debtor not found" user page
        Debtor.getUnknownDebtorId().then(id => {
          if (to.params.id === id) {
            console.log('Access to Debtor Not Found not allowed')
            next({ name: 'user-management' })
          }
          else {
            authGuard(to, from, next)
          }
        })
      },
      children: [
        {
          meta: {
            title: 'Debtor Profile',
          },
          name: 'debtor-profile',
          path: 'profile',
          props: (route) => ({
            ...route.params,
            userIsClient: userType.isClient(),
            userIsEmployee: userType.isEmployee(),
          }),
          component: () => import(/* webpackChunkName: "debtor-profile" */ './views/debtor/profile/debtor-profile.vue'),
        },
        {
          meta: {
            title: 'Processing Notes',
          },
          name: 'debtor-processing-notes',
          path: 'notes',
          props: (route) => ({
            ...route.params,
            pagePOV: 'debtor',
          }),
          component: () => import(/* webpackChunkName: "user-profile-contacts" */ './views/debtor/profile/debtor-processing-notes.vue'),
        },
        {
          meta: {
            title: 'Debtor Contacts',
          },
          name: 'debtor-contacts',
          path: 'contacts',
          props: (route) => ({
            ...route.params,
            pagePOV: 'debtor',
          }),
          component: () => import(/* webpackChunkName: "user-profile-contacts" */ './components/user-profile-contacts.vue'),
        },
        {
          meta: {
            title: 'Debtor Credit',
          },
          name: 'debtor-credit',
          path: 'credit',
          props: (route) => ({
            ...route.params,
            userIsClient: userType.isClient(),
            userIsEmployee: userType.isEmployee(),
          }),
          component: () => import(/* webpackChunkName: "debtor-credit" */ './views/debtor/profile/debtor-credit.vue'),
        },
        {
          meta: {
            title: 'Debtor Assignments',
          },
          name: 'debtor-assignments',
          title: 'Debtor Profile',
          path: 'assignments',
          props: (route) => ({
            ...route.params,
            pagePOV: 'debtor',
            userIsClient: userType.isClient(),
            userIsEmployee: userType.isEmployee(),
          }),
          component: () => import(/* webpackChunkName: "user-profile-assignments" */ './components/user-profile-assignments.vue'),
        },
        {
          meta: {
            requiresEmployee: true,
            title: 'Debtor Docs',
          },
          name: 'debtor-docs',
          path: 'docs',
          component: () => import(/* webpackChunkName: "user-docs" */ './views/user-docs.vue'),
        },
        {
          meta: {
            title: 'Debtor Manage',
          },
          name: 'debtor-manage',
          path: 'manage',
          props: (route) => ({
            ...route.params,
            pagePOV: 'debtor',
            userIsClient: userType.isClient(),
            userIsEmployee: userType.isEmployee(),
          }),
          component: () => import(/* webpackChunkName: "debtor-manage" */ './views/debtor/profile/debtor-manage.vue'),
        },
      ],
    },
    // Route is used to create a generic URL clients can be directed to which auto-redirects to
    // their banking page with the direct add modal displayed
    {
      meta: {
        mobileFriendly: true,
        requiresAuth: true,
        requiresClient: true,
        title: 'Direct Add Bank',
      },
      name: 'direct-add-bank',
      path: '/direct-add-bank',
      beforeEnter: (to, from, next) => {
        if (userType.isClient()) {
          next({
            name: 'dashboard-client-banking',
            params: { id: getInstance().user.client.id },
            query: { direct: true },
          })
        }
        else {
          next({ name: 'home' })
        }
      },
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: false,
        requiresAuth: true,
        requiresMaster: true,
        title: 'Edit Employee',
      },
      name: 'edit-employee',
      path: '/edit-employee',
      props: (route) => ({
        ...route.params,
      }),
      component: () => import(/* webpackChunkName: "edit-employee" */ './views/employee/create-edit-employee.vue'),
      beforeEnter: (to, from, next) => {
        // if this page is loaded w/o an employee prop, redirect them to user management
        if (!to.params.employee) {
          next({ name: 'user-management' })
        }
        else {
          next()
        }
      },
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: true,
        requiresAuth: true,
        requiresClient: true,
        title: '',
      },
      name: 'home',
      path: '/',
      redirect: () => {
        if (isMobile()) {
          return { name: 'home-mobile' }
        }
        return { name: 'pending' }
      },
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: true,
        requiresAuth: true,
        requiresClient: true,
        title: '',
      },
      name: 'home-mobile',
      path: '/home-mobile',
      component: () => import(/* webpackChunkName: "mobileHome" */ './views/mobile-home.vue'),
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: true,
        requiresAuth: true,
        requiresClient: true,
        title: '',
      },
      name: 'invoices',
      path: '/invoices',
      redirect: { name: 'pending' },
      component: () => import('./views/invoices/invoices.vue'),
      children: [
        {
          meta: {
            title: 'Approved Invoices',
          },
          name: 'approved',
          path: 'approved',
          props: (route) => ({
            ...route.params,
            userIsClient: userType.isClient(),
          }),
          component: () => import(/* webpackChunkName: "approved" */ './views/invoices/invoices-approved.vue'),
        },
        {
          meta: {
            title: 'Declined Invoices',
          },
          name: 'declined',
          path: 'declined',
          props: (route) => ({
            ...route.params,
            userIsClient: userType.isClient(),
          }),
          component: () => import(/* webpackChunkName: "declined" */ './views/invoices/invoices-declined.vue'),
        },
        {
          meta: {
            title: 'Paid Invoices',
          },
          name: 'paid',
          path: 'paid',
          props: (route) => ({
            ...route.params,
            userIsClient: userType.isClient(),
          }),
          component: () => import(/* webpackChunkName: "paid" */ './views/invoices/invoices-paid.vue'),
        },
        {
          meta: {
            title: 'Pending Invoices',
          },
          name: 'pending',
          path: 'pending',
          props: (route) => ({
            ...route.params,
            userIsClient: userType.isClient(),
          }),
          component: () => import(/* webpackChunkName: "pending" */ './views/invoices/invoices-pending.vue'),
        },
        {
          meta: {
            title: 'Universal Search',
          },
          name: 'search-results',
          path: 'search-results',
          props: (route) => ({
            ...route.params,
            userIsClient: userType.isClient(),
          }),
          component: () => import(/* webpackChunkName: "search-results" */ './views/invoices/invoice-search-results.vue'),
        },
      ],
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: false,
        requiresAuth: true,
        requiresEmployee: true,
        title: 'Invoice Details',
      },
      name: 'invoice-details',
      path: '/invoice-details/:id',
      props: (route) => ({
        ...route.params,
        userIsClient: userType.isClient(),
      }),
      component: () => import(/* webpackChunkName: "invoice-details" */ './views/invoices/invoice-details/invoice-details.vue'),
    },
    {
      meta: {
        guest: true,
        maintenanceFriendly: false,
        mobileFriendly: true,
        title: 'Logout',
      },
      name: 'logout',
      path: '/logout',
      props: true,
      beforeEnter: async () => {
        await getInstance().logout()
      },
    },
    {
      meta: {
        guest: true,
        maintenanceFriendly: true,
        mobileFriendly: true,
        title: 'Maintenance',
      },
      name: 'maintenance',
      path: '/maintenance',
      props: true,
      component: () => import(/* webpackChunkName: 'maintenance' */ './views/maintenance.vue'),
    },
    {
      meta: {
        guest: true,
        maintenanceFriendly: true,
        mobileFriendly: true,
        title: 'Permission Denied',
      },
      name: 'permission-denied',
      path: '/permission-denied',
      props: true,
      component: () => import(/* webpackChunkName: 'permission-denied' */ './views/permission-denied.vue'),
    },
    {
      meta: {
        guest: false,
        maintenanceFriendly: false,
        mobileFriendly: true,
        requiresAuth: true,
        requiresClient: true,
        title: 'Plaid Redirect URI',
      },
      name: 'plaid-redirect-uri',
      path: '/plaid-redirect-uri',
      redirect: (to) => {
        return {
          name: 'dashboard-client-banking',
          params: {
            id: getInstance().user.client.id,
            oAuthStateID: to.query.oauth_state_id,
          },
        }
      },
    },
    {
      meta: {
        guest: true,
        maintenanceFriendly: true,
        mobileFriendly: true,
        title: 'Privacy',
      },
      name: 'privacy',
      path: '/privacy',
      props: true,
      component: () => import(/* webpackChunkName: 'privacy' */ './views/legal/privacy.vue'),
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: true,
        requiresAuth: true,
        requiresClient: true,
        title: 'Refer and Earn',
      },
      name: 'referrals',
      path: '/referrals',
      props: true,
      component: () => import(/* webpackChunkName: 'referrals' */ './views/client/referrals.vue'),
    },
    {
      meta: {
        guest: true,
        maintenanceFriendly: true,
        mobileFriendly: true,
        title: 'Release Notes',
      },
      name: 'release-notes',
      path: '/internal/release-notes',
      component: () => import(/* webpackChunkName: "release-notes" */ './views/release-notes.vue'),
    },
    {
      meta: {
        guest: true,
        maintenanceFriendly: true,
        mobileFriendly: true,
        title: 'Sandbox',
      },
      name: 'sandbox',
      path: '/internal/test/sandbox',
      props: true,
      component: () => import(/* webpackChunkName: "sandbox" */ './views/test/sandbox.vue'),
    },
    {
      meta: {
        guest: true,
        maintenanceFriendly: true,
        mobileFriendly: false,
        title: 'Styleguide',
      },
      name: 'styleguide',
      path: '/internal/styleguide',
      component: () => import(/* webpackChunkName: "styleguide" */ './views/styleguide.vue'),
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: true,
        requiresAuth: true,
        requiresClient: true,
        title: 'Submit Invoice',
      },
      name: 'submit-invoice',
      path: '/submit-invoice',
      props: (route) => ({
        ...route.params,
        userIsClient: userType.isClient(),
      }),
      component: () => import(/* webpackChunkName: "submit-invoice" */ './views/invoices/submit-invoice.vue'),
    },
    {
      meta: {
        maintenanceFriendly: false,
        mobileFriendly: false,
        requiresAuth: true,
        requiresEmployee: true,
        title: 'User Management',
      },
      name: 'user-management',
      path: '/user-management',
      props: true,
      component: () => import(/* webpackChunkName: "user-management" */ './views/user-management.vue'),
    },
    {
      meta: {
        guest: true,
        maintenanceFriendly: false,
        mobileFriendly: true,
        title: 'Page Not Found',
      },
      name: 'page-not-found',
      path: '*',
      props: true,
      component: () => import(/* webpackChunkName: "404-page" */ './views/page-not-found.vue'),
    },
  ],
})

router.beforeEach(async (to, from, next) => {
  await authGuard(to, from, next)
})

// If user gets a "Loading chunk X failed", reload once
router.onError((error) => {
  console.log(error)
  const pattern = /^Loading chunk [0-9]+ failed/.test(error.message)
  if (pattern && !window.location.hash.includes('#retry')) {
    window.location.hash = '#retry'
    window.location.reload(true)
  }
})

export default router
